import { Component, inject, Input, OnChanges } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import {
  Chart,
  registerables,
  TooltipItem,
  Point,
  BubbleDataPoint,
} from 'chart.js';
import { CustomPieChartData } from 'src/app/impact/models/impactReportData';
Chart.register(...registerables);

@Component({
  selector: 'app-custom-pie-chart',
  templateUrl: './custom-pie-chart.component.html',
  styleUrl: './custom-pie-chart.component.scss',
  standalone: false,
})
export class CustomPieChartComponent implements OnChanges {
  @Input() title!: string;
  @Input() data?: CustomPieChartData;
  @Input() position: string = 'left';
  @Input() firstLine?: string;
  @Input() rowDirectionReverse: boolean = false;
  @Input() isPercentage?: boolean;
  @Input() isTransportDistance?: boolean;
  @Input() transportDistanceData?: {
    labels: string[];
    datasets: {
      backgroundColor: string[];
      data: number[];
    }[];
  };
  private translocoService = inject(TranslocoService);
  private formatCo2e = inject(FormatCo2eService);

  plugins: { id: string; beforeDraw: (chart: Chart) => void }[] = [];
  totalImpact: number = 0;

  options = {};

  totalKm: number = 0;

  transportOptions = {};
  transportPlugins: { id: string; beforeDraw: (chart: Chart) => void }[] = [];

  totalKilometers() {
    return this.transportDistanceData?.datasets?.[0]?.data
      ? this.transportDistanceData.datasets[0].data.reduce((a, b) => a + b, 0)
      : 0;
  }

  initializeGraphOption() {
    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: this.data?.total && this.data?.total > 0,
          callbacks: {
            title: (context: TooltipItem<'doughnut'>[]) => {
              const rawLabel = context[0].label;

              if (
                rawLabel &&
                typeof rawLabel === 'object' &&
                'type' in rawLabel
              ) {
                const typedLabel = rawLabel as { type: string };
                return this.translocoService.translate(
                  `categoryType.${typedLabel.type}`,
                );
              }

              return String(rawLabel);
            },

            label: (context: TooltipItem<'doughnut'>) => {
              const dataset = context.dataset;
              const total = dataset.data.reduce(
                (sum: number, val: number) => sum + val,
                0,
              );
              const currentValue = dataset.data[context.dataIndex];
              const percentage = ((currentValue / total) * 100).toFixed(2);

              const label = context.chart.data.labels?.[context.dataIndex];
              const getLabelText = (label: unknown): string => {
                if (label && typeof label === 'object' && 'type' in label) {
                  const typedLabel = label as { type: string };
                  return this.translocoService.translate(
                    `categoryType.${typedLabel.type}`,
                  );
                }
                return String(label);
              };

              const labelText = getLabelText(label);

              return `${labelText}: ${percentage}%`;
            },
          },
        },
      },
      cutout: '75%', // Defines the width of the donut
    };
  }

  initializeCenteredText() {
    this.plugins = [
      {
        id: 'centerText',
        beforeDraw: (chart: Chart) => {
          const { ctx, chartArea } = chart;
          const { left, right, top, bottom } = chartArea;

          ctx.save();
          const fontSize = (bottom - top) / 20;
          const primaryColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-primary-500')
            .trim();
          ctx.fillStyle = primaryColor || '#527A6D';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          const lines = [];
          if (this.firstLine) {
            lines.push({
              text: this.firstLine,
              font: `${fontSize}px sans-serif`,
            });
          }
          lines.push(
            {
              text: this.formatCo2e.formatValue(this.data?.total ?? 0),
              font: `bold ${fontSize * 2}px  sans-serif`,
            },
            {
              text: this.formatCo2e.getUnit(this.data?.total ?? 0),
              font: `${fontSize * 1.5}px sans-serif`,
            },
          );

          lines.forEach((line, index) => {
            ctx.font = line.font;
            ctx.fillText(
              line.text,
              (left + right) / 2,
              (top + bottom) / 2 -
                ((lines.length - 1) / 2) * (fontSize * 2 + 5) +
                index * (fontSize * 2 + 5),
            );
          });

          ctx.restore();
        },
      },
    ];
  }

  initializeTransportGraphOption() {
    this.transportOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: TooltipItem<'doughnut'>) => {
              const label =
                context.chart.data.labels?.[context.dataIndex] ?? '';
              const value = context.dataset.data[context.dataIndex];
              this.totalKm = value;

              let labelText = label;
              if (typeof label === 'string') {
                labelText = this.translocoService.translate(
                  `categoryType.${label}`,
                );
                if (typeof labelText === 'string' && labelText.length > 25) {
                  labelText = labelText.slice(0, 21) + '... ';
                }
              }
              return `${labelText}: ${value} km`;
            },
          },
        },
      },
      cutout: '75%',
    };
  }

  private extractNumber(
    val:
      | number
      | [number, number]
      | { y?: number }
      | Point
      | BubbleDataPoint
      | null,
  ): number {
    if (typeof val === 'number') return val;
    if (Array.isArray(val) && val.length === 2 && typeof val[1] === 'number')
      return val[1];
    if (
      val &&
      typeof val === 'object' &&
      'y' in val &&
      typeof val.y === 'number'
    )
      return val.y;
    return 0;
  }

  initializeTransportCenteredText() {
    this.transportPlugins = [
      {
        id: 'centerText',
        beforeDraw: (chart: Chart) => {
          const { ctx, chartArea } = chart; // Récupère la zone de dessin réelle
          const { left, right, top, bottom } = chartArea;

          ctx.save();
          const fontSize = (bottom - top) / 20; // Ajuste la taille de la police en fonction de la hauteur du graphique
          const primaryColor = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-primary-500')
            .trim();
          ctx.fillStyle = primaryColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          // Texte multi-lignes

          const lines: { text: string; font: string }[] = [];
          if (this.firstLine) {
            lines.push({
              text: this.firstLine,
              font: `${fontSize}px sans-serif`,
            });
          }

          this.totalKm = chart.data.datasets[0].data.reduce(
            (sum: number, val) => sum + this.extractNumber(val),
            0,
          );

          lines.push(
            {
              text: this.totalKm.toLocaleString('fr-FR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              font: `bold ${fontSize * 2}px sans-serif`,
            },
            {
              text: this.translocoService.translate(`impactReport.kmTotal`),
              font: `${fontSize * 1.5}px sans-serif`,
            },
          );

          // Dessine chaque ligne
          lines.forEach((line, index) => {
            ctx.font = line.font;
            ctx.fillText(
              line.text,
              (left + right) / 2,
              (top + bottom) / 2 -
                ((lines.length - 1) / 2) * (fontSize * 2 + 5) +
                index * (fontSize * 2 + 5),
            );
          });

          ctx.restore();
        },
      },
    ];
  }

  ngOnChanges() {
    this.initializeGraphOption();
    this.initializeCenteredText();

    if (this.isTransportDistance) {
      this.initializeTransportGraphOption();
      this.initializeTransportCenteredText();
    }
  }
}
